import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import AnimatedDemo from "@/components/AnimatedDemo";
import ContactForm from "@/components/ContactForm";
import {
  Sparkles,
  Check,
  FileText,
  Receipt,
  Calendar,
  Mail,
  Wallet,
  Users,
  ArrowRight,
  ShieldCheck,
  Zap,
  Star,
  Quote,
} from "lucide-react";

const HERO_IMG = null;

const features = [
  {
    icon: FileText,
    title: "Presupuestos y facturas",
    desc: "Crea, envía y convierte presupuestos aprobados en facturas con un solo clic. Numeración correlativa automática.",
  },
  {
    icon: Mail,
    title: "Envío automático a tu gestoría",
    desc: "Configura el email de tu asesoría una vez y cada factura emitida se envía automáticamente con PDF + XML Facturae.",
  },
  {
    icon: Calendar,
    title: "Calendario fiscal precargado",
    desc: "Modelos 303, 130, 390, 347, cuota de autónomos y renta. Nunca más vas a olvidar una fecha clave.",
  },
  {
    icon: Wallet,
    title: "Control de cobros",
    desc: "Panel de facturas pagadas, pendientes y vencidas. Recordatorios automáticos a clientes morosos.",
  },
  {
    icon: Users,
    title: "Base de clientes",
    desc: "Alta rápida con CIF/NIF y datos fiscales. Búsqueda instantánea cuando emitas una factura nueva.",
  },
  {
    icon: ShieldCheck,
    title: "PDF y Facturae XML",
    desc: "Documentos profesionales listos para enviar. Formato compatible con Facturae 3.2.1 para la Administración.",
  },
];

const plans = [
  {
    name: "Gratis",
    price: "0€",
    period: "para siempre",
    lookup: null,
    highlight: false,
    features: [
      "5 facturas al mes",
      "Clientes ilimitados",
      "Calendario fiscal",
      "PDF y XML",
    ],
    cta: "Empezar gratis",
  },
  {
    name: "Pro",
    price: "9,99€",
    period: "/mes",
    lookup: "pro_monthly",
    highlight: true,
    features: [
      "Facturas ilimitadas",
      "Envío automático a gestoría",
      "Recordatorios automáticos",
      "Presupuestos ilimitados",
      "Soporte prioritario",
    ],
    cta: "Empezar con Pro",
  },
  {
    name: "Business",
    price: "19,99€",
    period: "/mes",
    lookup: "business_monthly",
    highlight: false,
    features: [
      "Todo lo de Pro",
      "Cobros online con Stripe (próximamente)",
      "Multi-usuario (gestoría)",
      "Firma electrónica (próximamente)",
      "Onboarding personalizado",
    ],
    cta: "Empezar con Business",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-600 grid place-items-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-lg tracking-tight text-slate-900 notranslate" translate="no">
                CHRONOVIA
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest -mt-0.5">
                Facturación
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">
              Características
            </a>
            <a href="#pricing" className="hover:text-slate-900">
              Precios
            </a>
            <a href="#contacto" className="hover:text-slate-900">
              Contacto
            </a>
            <a href="#faq" className="hover:text-slate-900">
              Preguntas
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="text-sm text-slate-700 hover:text-slate-900"
              data-testid="nav-login-link"
            >
              Entrar
            </Link>
            <Link to="/register">
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="nav-register-btn"
              >
                Probar gratis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="gradient-mesh">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-50 mb-6">
              <Zap className="w-3 h-3 mr-1" /> Hecho para autónomos españoles
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Deja el Excel.{" "}
              <span className="text-blue-600">Factura como una empresa.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Presupuestos, facturas, envío automático a tu gestoría y el
              calendario fiscal español, todo en una sola herramienta pensada
              para autónomos y pequeños negocios.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 h-12 px-6"
                  data-testid="hero-cta-register"
                >
                  Empieza gratis
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6 border-slate-300"
                  data-testid="hero-cta-pricing"
                >
                  Ver planes
                </Button>
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" /> Sin tarjeta
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" /> Español 100%
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600" /> PDF + XML
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-tr from-blue-500/10 to-indigo-500/10 rounded-3xl blur-2xl" />
            <div className="relative">
              <AnimatedDemo />
            </div>
            <div className="hidden lg:block absolute -bottom-6 -left-6 bg-white rounded-xl border border-slate-200 shadow-lg p-4 w-56 z-10">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                Ahorro semanal
              </div>
              <div className="font-display text-2xl font-bold text-slate-900">
                4h 20min
              </div>
              <div className="text-xs text-emerald-600 mt-1">
                vs. gestión manual
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-8 border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center text-xs font-medium uppercase tracking-widest text-slate-500 mb-6">
            Compatible con los principales bancos españoles
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              { name: "Santander", color: "#EC0000" },
              { name: "BBVA", color: "#004481" },
              { name: "CaixaBank", color: "#007FBA" },
              { name: "ING", color: "#FF6200" },
              { name: "Sabadell", color: "#00629B" },
              { name: "Bankinter", color: "#F26E00" },
              { name: "Openbank", color: "#FF0000" },
              { name: "Revolut", color: "#0075EB" },
              { name: "N26", color: "#26A17B" },
            ].map((b) => (
              <div
                key={b.name}
                className="font-display text-lg font-bold tracking-tight opacity-70 hover:opacity-100 transition"
                style={{ color: b.color }}
              >
                {b.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
              Todo en uno
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Diseñado para que dediques menos tiempo a la administración.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div
                key={f.title}
                className="border border-slate-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-sm transition"
                data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center mb-4">
                  <f.icon className="w-5 h-5 text-blue-600" strokeWidth={2} />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-2">
                  {f.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
              Lo dicen quienes ya lo usan
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Autónomos que dejaron de perder los domingos con la administración.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "María López",
                role: "Diseñadora freelance · Madrid",
                initials: "ML",
                color: "#2563EB",
                text: "Antes tardaba una hora en preparar una factura y otra media en mandarla a la gestoría. Ahora la emito en 30 segundos y la gestoría la recibe sola. Es un antes y un después.",
                rating: 5,
              },
              {
                name: "Javier Ruiz",
                role: "Desarrollador web · Valencia",
                initials: "JR",
                color: "#0EA5E9",
                text: "La conciliación bancaria automática me ha ahorrado revisar movimientos uno a uno. Cuando entra el pago, la factura se marca como pagada sola. Magia.",
                rating: 5,
              },
              {
                name: "Ana Martínez",
                role: "Consultora fiscal · Sevilla",
                initials: "AM",
                color: "#10B981",
                text: "El calendario fiscal con avisos de los modelos 303, 130 y la cuota de autónomos me ha salvado de más de un recargo. La app pide sola cuando toca cada cosa.",
                rating: 5,
              },
            ].map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-7 border border-slate-200 relative"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="w-8 h-8 text-blue-100 absolute top-4 right-4" />
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star
                      key={s}
                      className="w-4 h-4 fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed text-sm">
                  "{t.text}"
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-full grid place-items-center text-white font-bold text-sm"
                    style={{ background: t.color }}
                  >
                    {t.initials}
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 text-sm">
                      {t.name}
                    </div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 bg-white rounded-2xl p-8 border border-slate-200">
            {[
              { v: "4h+", l: "ahorradas por semana" },
              { v: "30s", l: "para emitir una factura" },
              { v: "0€", l: "para empezar" },
              { v: "4,9/5", l: "valoración media" },
            ].map((s) => (
              <div key={s.l} className="text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-blue-600">
                  {s.v}
                </div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
              Precios sencillos
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
              Prueba gratis, escala cuando lo necesites.
            </h2>
            <p className="mt-4 text-slate-600 max-w-lg mx-auto">
              Sin permanencia. Cancelas en cualquier momento.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => (
              <div
                key={p.name}
                className={`bg-white rounded-2xl p-8 border ${
                  p.highlight
                    ? "border-blue-600 shadow-xl ring-2 ring-blue-100"
                    : "border-slate-200"
                }`}
                data-testid={`pricing-plan-${p.name.toLowerCase()}`}
              >
                {p.highlight && (
                  <Badge className="bg-blue-600 mb-4">Más elegido</Badge>
                )}
                <h3 className="font-display font-bold text-2xl text-slate-900">
                  {p.name}
                </h3>
                <div className="mt-4 flex items-baseline">
                  <span className="font-display text-4xl font-extrabold text-slate-900">
                    {p.price}
                  </span>
                  <span className="ml-2 text-slate-500 text-sm">{p.period}</span>
                </div>
                <ul className="mt-6 space-y-3 text-sm">
                  {p.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="text-slate-700">{feat}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.lookup ? `/register?plan=${p.lookup}` : "/register"}
                  className="block mt-8"
                >
                  <Button
                    className={`w-full h-11 ${
                      p.highlight
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-slate-900 hover:bg-slate-800"
                    }`}
                    data-testid={`plan-cta-${p.name.toLowerCase()}`}
                  >
                    {p.cta}
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contacto */}
      <section id="contacto" className="py-20 lg:py-24 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">
                Estamos aquí
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                ¿Alguna duda antes de empezar?
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Escríbenos y te contestamos personalmente en menos de 24 h. Sin
                bots, sin formularios kilométricos.
              </p>
              <a
                href="mailto:info@autonomopro.online"
                className="mt-6 inline-flex items-center gap-3 group"
                data-testid="landing-contact-email"
              >
                <div className="w-11 h-11 rounded-lg bg-blue-600 grid place-items-center group-hover:scale-105 transition">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Email directo</div>
                  <div className="font-display font-bold text-lg text-slate-900 group-hover:text-blue-600 transition">
                    info@autonomopro.online
                  </div>
                </div>
              </a>
              <div className="mt-8 text-sm text-slate-500 leading-relaxed">
                <b className="text-slate-700">Horario de soporte:</b>
                <br />
                Lunes a viernes · 9:00 – 19:00 (España)
              </div>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="faq" className="py-20 lg:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Empieza a facturar en 60 segundos
          </h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
            Regístrate, añade tus datos fiscales y el email de tu gestoría.
            Nosotros hacemos el resto.
          </p>
          <Link to="/register" className="inline-block mt-8">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 h-12 px-8"
              data-testid="footer-cta-register"
            >
              Crear cuenta gratis
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-10 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-blue-600 grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-slate-900 notranslate" translate="no">
              CHRONOVIA-FACTURACION
            </span>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6 text-sm text-slate-500">
            <a
              href="mailto:info@autonomopro.online"
              className="hover:text-blue-600 transition flex items-center gap-1.5"
              data-testid="footer-contact-email"
            >
              <Mail className="w-3.5 h-3.5" />
              info@autonomopro.online
            </a>
            <span className="hidden md:inline text-slate-300">·</span>
            <span>© {new Date().getFullYear()} Hecho para autónomos españoles</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
